import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../lib/api.js";
import { salesApi } from "../client.js";
import type { SxStyle } from "../leads/heatStyles.js";
import type { SalesAgentHealth } from "@xop/dashboard-shared";

/**
 * Health surface for the autonomous sales agent. Polls
 * `/api/sales/health` every 60 s. Quiet green dot when everything is
 * fine, coloured + clickable when something is wrong — clicking opens
 * a popover with upstream URL, status, body excerpt, and an actionable
 * hint keyed by the diagnostic code.
 *
 * Mirrors `SupportLayout`'s `BackendHealthPill` so the staff user sees
 * a consistent affordance across the dashboard.
 */
export function SalesHealthPill(): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const health = useQuery({
    queryKey: ["sales", "health"],
    queryFn: salesApi.getHealth,
    refetchInterval: 60_000,
    retry: false,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const { tone, label, clickable } = pillState(
    health.isLoading ? "loading" : health.isError ? "fetch-error" : "ok",
    health.data,
    health.error,
  );

  return (
    <Box ref={containerRef} sx={{ position: "relative", display: "inline-flex" }}>
      <ButtonBase
        onClick={() => clickable && setOpen((o) => !o)}
        disabled={!clickable}
        aria-label="Sales agent status"
        aria-expanded={clickable ? open : undefined}
        title={clickable ? "Click for details" : undefined}
        sx={[
          {
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            borderRadius: 999,
            px: 1.5,
            py: 0.5,
            fontSize: 12,
            fontWeight: 500,
          },
          toneSx(tone),
          clickable
            ? { cursor: "pointer", "&:hover": { filter: "brightness(0.95)" } }
            : { cursor: "default" },
        ]}
      >
        <Box component="span" sx={[{ height: 6, width: 6, borderRadius: "50%" }, dotSx(tone)]} />
        {label}
        {clickable && (
          <Box component="svg" viewBox="0 0 8 8" sx={{ height: 8, width: 8, opacity: 0.7 }}>
            <path d="M1 3l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </Box>
        )}
      </ButtonBase>
      {open && clickable && (
        <DetailsPanel data={health.data} error={health.error} />
      )}
    </Box>
  );
}

interface PillState {
  tone: "good" | "warn" | "bad" | "neutral";
  label: string;
  clickable: boolean;
}

function pillState(
  phase: "loading" | "fetch-error" | "ok",
  data: SalesAgentHealth | undefined,
  error: unknown,
): PillState {
  if (phase === "loading") {
    return { tone: "neutral", label: "Checking agent…", clickable: false };
  }
  if (phase === "fetch-error") {
    const code = error instanceof ApiError ? error.code : undefined;
    return {
      tone: "bad",
      label: code ? `Dashboard server: ${code}` : "Dashboard server unreachable",
      clickable: true,
    };
  }
  if (!data) {
    return { tone: "warn", label: "No health data", clickable: true };
  }
  // Operator pause is the top-level truth and overrides the health code:
  // a paused agent still reports SALES_AGENT_OK upstream, but it is NOT
  // "healthy / live" — show one honest "Paused" pill instead.
  if (data.is_paused) {
    return { tone: "warn", label: "Paused", clickable: true };
  }
  // Daily budget spent → the agent is asleep until the reset. It is NOT
  // "healthy / live", but this is intentional (a cost cap), not a fault — a
  // muted neutral "Sleeping" pill, clickable for the wake time.
  if (data.budget_exhausted) {
    return { tone: "neutral", label: "Sleeping", clickable: true };
  }
  switch (data.code) {
    case "SALES_AGENT_OK":
      // Clickable even when green. "Healthy" here only means the agent
      // ANSWERED — it says nothing about whether the autonomous loop is
      // making progress. An agent that is up, funded and silent for hours
      // reports SALES_AGENT_OK, and while this pill was unclickable in that
      // state there was no way at all to ask why from a browser
      // (2026-08-28). The panel now carries the freshness + signing-key
      // diagnostics, so the green pill is the way in, not a dead end.
      return { tone: "good", label: "Agent healthy", clickable: true };
    case "SALES_AGENT_BOOTSTRAPPING":
      return { tone: "warn", label: "Bootstrapping", clickable: true };
    case "SALES_AGENT_NO_DATA_YET":
      return { tone: "warn", label: "No leads yet", clickable: true };
    case "SALES_AGENT_DEGRADED":
      return {
        tone: "warn",
        label: `Agent HTTP ${data.upstream_status ?? "?"}`,
        clickable: true,
      };
    case "SALES_AGENT_UNREACHABLE":
      return { tone: "bad", label: "Agent unreachable", clickable: true };
    case "SALES_AGENT_NOT_CONFIGURED":
      return { tone: "warn", label: "Agent not configured", clickable: true };
    default:
      return { tone: "warn", label: "Status unknown", clickable: true };
  }
}

/** Pill surface tint per tone (was the bg-[tone]/10 + text-[tone]-700 class set). */
function toneSx(tone: PillState["tone"]): SxStyle {
  switch (tone) {
    case "good":
      return {
        bgcolor: (t: Theme) => alpha(t.palette.success.main, 0.1),
        color: "success.main",
      };
    case "warn":
      return {
        bgcolor: (t: Theme) => alpha(t.palette.warning.main, 0.1),
        color: "warning.main",
      };
    case "bad":
      return {
        bgcolor: (t: Theme) => alpha(t.palette.error.main, 0.1),
        color: "error.main",
      };
    case "neutral":
    default:
      return { bgcolor: "surfaceSoft", color: "text.disabled" };
  }
}

function dotSx(tone: PillState["tone"]): SxStyle {
  switch (tone) {
    case "good":
      return { bgcolor: "success.main" };
    case "warn":
      return { bgcolor: "warning.main" };
    case "bad":
      return { bgcolor: "error.main" };
    case "neutral":
    default:
      return { bgcolor: "text.disabled" };
  }
}

function DetailsPanel({
  data,
  error,
}: {
  data: SalesAgentHealth | undefined;
  error: unknown;
}): JSX.Element {
  const code = data?.code ?? (error instanceof ApiError ? error.code : undefined);
  return (
    <Box
      role="dialog"
      aria-label="Sales agent status details"
      sx={{
        position: "absolute",
        right: 0,
        top: "100%",
        zIndex: 30,
        mt: 1,
        width: "26rem",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        p: 2,
        fontSize: 12,
        boxShadow: (t: Theme) => t.shadows[3],
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" }}>
        {data?.budget_exhausted ? "Agent sleeping — daily budget spent" : titleFor(code)}
      </Typography>
      <Typography sx={{ mt: 0.5, color: "text.secondary" }}>
        {data?.budget_exhausted
          ? "The agent has spent its daily LLM budget and is idling until the reset. It resumes automatically — no action needed."
          : hintFor(code)}
      </Typography>
      <Box component="dl" sx={{ mt: 1.5, display: "grid", gap: 1 }}>
        {data?.budget_exhausted && data?.budget_resets_at && (
          <Row label="Wakes at">
            <code>{new Date(data.budget_resets_at).toLocaleString()}</code>
          </Row>
        )}
        {data?.upstream_url && (
          <Row label="Upstream URL"><Box component="code" sx={{ wordBreak: "break-all" }}>{data.upstream_url}</Box></Row>
        )}
        {typeof data?.upstream_status === "number" && (
          <Row label="HTTP status"><code>{data.upstream_status}</code></Row>
        )}
        {data?.reason && (
          <Row label="Reason"><Box component="code" sx={{ wordBreak: "break-all" }}>{data.reason}</Box></Row>
        )}
        {data?.upstream_body_snippet && (
          <Row label="Body snippet">
            <Box component="pre" sx={{ maxHeight: 128, overflow: "auto", borderRadius: 1, bgcolor: "surfaceSoft", p: 1, fontSize: 10 }}>
              {data.upstream_body_snippet}
            </Box>
          </Row>
        )}
        {typeof data?.has_company_profile === "boolean" && (
          <Row label="Self-knowledge">
            {data.has_company_profile ? "loaded" : "not yet ingested"}
          </Row>
        )}
        {typeof data?.has_strategy === "boolean" && (
          <Row label="Strategy memo">
            {data.has_strategy ? "written" : "not yet written"}
          </Row>
        )}
        {typeof data?.has_leads === "boolean" && (
          <Row label="Leads">
            {data.has_leads ? "discovered" : "none yet"}
          </Row>
        )}

        {/* Autonomy diagnostics. "Agent healthy" only means it ANSWERED;
            these say whether it is actually getting anything done. They are
            the browser-readable form of the runbook's
            `curl …:8004/health | jq '.auth, .freshness'`, which needs VPC
            access an operator on a laptop does not have. */}
        {data?.last_master_step_age_s !== undefined && (
          <Row label="Last step">{ageLabel(data.last_master_step_age_s)}</Row>
        )}
        {data?.last_lead_age_s !== undefined && (
          <Row label="Last lead">{ageLabel(data.last_lead_age_s)}</Row>
        )}
        {typeof data?.recent_tick_errors === "number" && (
          <Row label="Tick errors">
            <Box
              component="span"
              sx={{ color: data.recent_tick_errors > 0 ? "error.main" : "text.primary" }}
            >
              {data.recent_tick_errors > 0
                ? `${data.recent_tick_errors} in the last 15 min — the loop cannot think`
                : "none in the last 15 min"}
            </Box>
          </Row>
        )}
        {data?.last_tick_error && (
          <Row label="Last error">
            <Box component="code" sx={{ wordBreak: "break-word" }}>{data.last_tick_error}</Box>
          </Row>
        )}
        {typeof data?.gateway_ok === "boolean" && (
          <Row label="AI gateway">
            <Box component="span" sx={{ color: data.gateway_ok ? "text.primary" : "error.main" }}>
              {data.gateway_ok ? "reachable" : `unreachable${data.gateway_error ? ` — ${data.gateway_error}` : ""}`}
            </Box>
          </Row>
        )}
        {typeof data?.auth?.stable_signing_key === "boolean" && (
          <Row label="Signing key">
            <Box
              component="span"
              sx={{ color: data.auth.stable_signing_key ? "text.primary" : "error.main" }}
            >
              {data.auth.stable_signing_key
                ? `stable${data.auth.pem_env ? ` (${data.auth.pem_env})` : ""}`
                : "EPHEMERAL — the worker's gateway tokens are rejected"}
            </Box>
          </Row>
        )}
        {data?.auth?.detail && (
          <Row label="Signing detail">
            <Box component="span" sx={{ wordBreak: "break-word" }}>{data.auth.detail}</Box>
          </Row>
        )}
        {data?.mission_queue && (
          <Row label="Missions">
            <code>
              {data.mission_queue.pending} pending · {data.mission_queue.running} running ·{" "}
              {data.mission_queue.done} done
            </code>
          </Row>
        )}
        {data?.error && (
          <Row label="Error"><code>{data.error}</code></Row>
        )}
        {error instanceof ApiError && (
          <Row label="Dashboard error">
            <code>{error.status} · {error.code ?? "—"} · {error.message}</code>
          </Row>
        )}
      </Box>
    </Box>
  );
}

/** "4 min ago" / "never" — a freshness age the operator can read. `null` is
 *  the agent saying the thing has never happened, which is different from the
 *  key being absent (an older agent build). */
function ageLabel(seconds: number | null | undefined): string {
  if (seconds === null) return "never";
  if (typeof seconds !== "number") return "—";
  if (seconds < 90) return `${Math.round(seconds)} s ago`;
  const mins = Math.round(seconds / 60);
  if (mins < 90) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "110px 1fr", alignItems: "baseline", gap: 1 }}>
      <Box component="dt" sx={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        {label}
      </Box>
      <Box component="dd" sx={{ minWidth: 0, m: 0 }}>{children}</Box>
    </Box>
  );
}

function titleFor(code: string | undefined): string {
  switch (code) {
    case "SALES_AGENT_OK":             return "Agent healthy";
    case "SALES_AGENT_BOOTSTRAPPING":  return "Agent is bootstrapping";
    case "SALES_AGENT_NO_DATA_YET":    return "Agent has no leads yet";
    case "SALES_AGENT_DEGRADED":       return "Agent reachable but reporting trouble";
    case "SALES_AGENT_UNREACHABLE":    return "Agent unreachable";
    case "SALES_AGENT_NOT_CONFIGURED": return "Agent URL not configured";
    default:                            return "Agent status unknown";
  }
}

function hintFor(code: string | undefined): string {
  switch (code) {
    case "SALES_AGENT_OK":
      return "The autonomous loop is running and the agent reports a healthy heartbeat.";
    case "SALES_AGENT_BOOTSTRAPPING":
      return "First-boot ingest of xopnetworks.com into the knowledge base. Leads will appear once it's done.";
    case "SALES_AGENT_NO_DATA_YET":
      return "The agent is healthy but hasn't surfaced any leads yet — usually means the first vertical sweep hasn't completed.";
    case "SALES_AGENT_DEGRADED":
      return "The agent answered, but its self-check is failing. Check docker compose logs for the worker / beat / db services.";
    case "SALES_AGENT_UNREACHABLE":
      return "The dashboard server could not reach the agent. Confirm the FastAPI service is running and that SALES_AGENT_URL points at it.";
    case "SALES_AGENT_NOT_CONFIGURED":
      return "Set SALES_AGENT_URL in the dashboard server's .env (e.g. http://host.docker.internal:8004) and restart.";
    default:
      return "Open dev-tools and inspect /api/sales/health for the raw payload.";
  }
}
